<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import { KET_QUA_DANG_DICH } from 'dashboard/store/modules/conversations/actions/messageTranslateActions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ReportCaptainMessageDialog from './ReportCaptainMessageDialog.vue';

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    ReportCaptainMessageDialog,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo', 'chuyenTiep'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      // BonBoZ: muc "Dich…" dang bung danh sach ngon ngu hay chua.
      hienNgonNgu: false,
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      // BonBoZ: thu danh sach ngon ngu dich ve trang thai mac dinh — component
      // nay song suot doi tin nhan (chi ContextMenu con bi go), khong reset thi
      // lan mo sau menu van bung san.
      this.hienNgonNgu = false;
      this.$emit('close', e);
    },
    // BonBoZ: nguoi truc CHON ngon ngu dich ngay tren menu thay vi he thong
    // doan theo locale giao dien — truoc day khong co cach nao kiem soat.
    //
    // Menu DONG NGAY khi bam, dich chay nen: LLM mat vai giay, giu menu treo
    // trong luc do thi nguoi truc tuong dashboard dung hinh. Ket qua tu hien
    // duoi bong bong (store action ghi thang vao tin); guard chong bam chong
    // nam trong store de moi loi vao (menu nao, tin nao) cung di qua mot cua.
    async handleTranslate(targetLanguage) {
      const thamSo = {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      };
      this.handleClose();
      try {
        const noiDung = await this.$store.dispatch('translateMessage', thamSo);
        if (noiDung === KET_QUA_DANG_DICH) {
          useAlert(this.$t('BONBOZ.DICH.DANG_DICH'));
          return;
        }
        // May chu tra 200 kem noi dung rong khi chua cau hinh khoa dich —
        // khong phai loi HTTP nen `catch` ben duoi khong bao gio chay.
        if (!noiDung) {
          useAlert(this.$t('BONBOZ.DICH.KHONG_DICH_DUOC'));
          return;
        }
        useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      } catch (error) {
        useAlert(parseAPIErrorResponse(error));
      }
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
    },
    // BonBoZ: chuyển tiếp tin sang hội thoại khác.
    handleChuyenTiep() {
      this.$emit('chuyenTiep', this.message);
      this.handleClose();
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    openReportDialog() {
      this.handleClose();
      this.$refs.reportDialog?.open();
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <!-- BonBoZ: ngay dưới "Trả lời tin này", đúng thứ tự Pancake.
             `arrow-redo` chứ không phải `arrow-forward`: FluentIcon tra bảng
             bằng `<ten>-<type>`, mà `arrow-forward-outline` không có trong
             `dashboard-icons.json` nên mục này hiện KHÔNG icon, lệch hẳn so với
             các mục khác. -->
        <MenuItem
          :option="{
            icon: 'arrow-redo',
            label: $t('BONBOZ.TIN_NHAN.CHUYEN_TIEP'),
          }"
          variant="icon"
          @click.stop="handleChuyenTiep"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <!-- BonBoZ: "Dich…" bung ra chon ngon ngu dich ngay trong menu —
             nguoi truc kiem soat dich sang gi thay vi he thong doan theo locale
             giao dien. "Tieng Viet" con kiem luon viec THEM DAU cho tin tieng
             Viet khong dau (prompt phia server lo). Muon them thu tieng: them
             mot MenuItem + mot key BONBOZ.DICH.* la xong. -->
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('BONBOZ.DICH.MENU'),
          }"
          variant="icon"
          @click.stop="hienNgonNgu = !hienNgonNgu"
        />
        <template v-if="enabledOptions['translate'] && hienNgonNgu">
          <div class="ltr:pl-6 rtl:pr-6">
            <MenuItem
              :option="{
                icon: 'arrow-right',
                label: $t('BONBOZ.DICH.SANG_VI'),
              }"
              variant="icon"
              @click.stop="handleTranslate('vi')"
            />
          </div>
          <div class="ltr:pl-6 rtl:pr-6">
            <MenuItem
              :option="{
                icon: 'arrow-right',
                label: $t('BONBOZ.DICH.SANG_EN'),
              }"
              variant="icon"
              @click.stop="handleTranslate('en')"
            />
          </div>
        </template>
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['report']" />
        <MenuItem
          v-if="enabledOptions['report']"
          :option="{
            icon: 'warning',
            label: $t('CONVERSATION.CONTEXT_MENU.REPORT_MESSAGE.LABEL'),
          }"
          variant="icon"
          @click.stop="openReportDialog"
        />
        <hr v-if="enabledOptions['delete']" />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
    <ReportCaptainMessageDialog
      v-if="enabledOptions['report']"
      ref="reportDialog"
      :message-id="messageId"
    />
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  :deep(.modal-container) {
    @apply max-w-[30rem];

    h2 {
      @apply font-medium text-base;
    }
  }
}
</style>
